import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider } from './context/AppContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import Skeleton from './components/ui/Skeleton';
import ScrollToTop from './components/ScrollToTop';

// Eager load critical pages
import Landing from './pages/Landing';
import NotFound from './pages/NotFound';

// Lazy load other pages for code splitting
const OptionSelection = lazy(() => import('./pages/OptionSelection'));
const Home = lazy(() => import('./pages/Home'));
const AboutYouForm = lazy(() => import('./pages/AboutYouForm'));
const VideoLengthSelection = lazy(() => import('./pages/VideoLengthSelection'));
const TemplateSelection = lazy(() => import('./pages/TemplateSelection'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions'));
const Payment = lazy(() => import('./pages/Payment'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const PaymentFail = lazy(() => import('./pages/PaymentFail'));

// Video Creation Flow - Lazy loaded
const VideoTitle = lazy(() => import('./pages/VideoCreation/VideoTitle'));
const Questions = lazy(() => import('./pages/VideoCreation/Questions'));
const Credits = lazy(() => import('./pages/VideoCreation/Credits'));
const CoverPhoto = lazy(() => import('./pages/VideoCreation/CoverPhoto'));
const VerifyPersonal = lazy(() => import('./pages/VideoCreation/VerifyPersonal'));
const VerifyVideo = lazy(() => import('./pages/VideoCreation/VerifyVideo'));
const VerifyFinal = lazy(() => import('./pages/VideoCreation/VerifyFinal'));
const Submit = lazy(() => import('./pages/VideoCreation/Submit'));
const Confirmation = lazy(() => import('./pages/VideoCreation/Confirmation'));

// New Freemium Pages - Lazy loaded
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Subscription = lazy(() => import('./pages/Subscription'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));

// Greeting Card Flow - Lazy loaded
const GreetingTemplateSelection = lazy(() => import('./pages/Greeting/TemplateSelection'));
const GreetingRecording = lazy(() => import('./pages/Greeting/RecordingPage'));
const GreetingPreview = lazy(() => import('./pages/Greeting/PreviewAndShare'));

// Authentication Pages - Lazy loaded
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));

// About Us - Lazy loaded
const AboutUs = lazy(() => import('./pages/AboutUs'));

// Features Page - Lazy loaded
const Features = lazy(() => import('./pages/Features'));

// Contact & Privacy Pages - Lazy loaded
const Contact = lazy(() => import('./pages/Contact'));
const Privacy = lazy(() => import('./pages/Privacy'));
const CommunityGuidelines = lazy(() => import('./pages/CommunityGuidelines'));

// Diagnostic Page - Lazy loaded
const DiagnosticPage = lazy(() => import('./pages/DiagnosticPage'));

// Loading component for lazy-loaded routes
const PageLoader = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
    <div className="max-w-3xl w-full space-y-4">
      <Skeleton className="h-12 w-3/4 mx-auto" />
      <Skeleton className="h-64 w-full" />
      <Skeleton className="h-32 w-full" />
    </div>
  </div>
);

function App() {
  return (
    <AppProvider>
      <Router>
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/option-selection" element={<OptionSelection />} />
            <Route path="/home" element={<Home />} />
            
            {/* Authentication */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            
            {/* Diagnostic */}
            <Route path="/diagnostic" element={<DiagnosticPage />} />
            
            {/* Dashboard & Subscription */}
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/subscription" element={<Subscription />} />
            
            {/* Admin Dashboard */}
            <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
            
            {/* Legacy Video Flow */}
            <Route path="/about-you" element={<AboutYouForm />} />
            <Route path="/video-length" element={<VideoLengthSelection />} />
            <Route path="/template-selection" element={<TemplateSelection />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/payment" element={<Payment />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/payment-fail" element={<PaymentFail />} />
            
            {/* Protected Video Creation Routes */}
            <Route path="/video-creation/title" element={<ProtectedRoute><VideoTitle /></ProtectedRoute>} />
            <Route path="/video-creation/questions" element={<ProtectedRoute><Questions /></ProtectedRoute>} />
            <Route path="/video-creation/credits" element={<ProtectedRoute><Credits /></ProtectedRoute>} />
            <Route path="/video-creation/cover-photo" element={<ProtectedRoute><CoverPhoto /></ProtectedRoute>} />
            <Route path="/video-creation/verify/personal" element={<ProtectedRoute><VerifyPersonal /></ProtectedRoute>} />
            <Route path="/video-creation/verify/video" element={<ProtectedRoute><VerifyVideo /></ProtectedRoute>} />
            <Route path="/video-creation/verify/final" element={<ProtectedRoute><VerifyFinal /></ProtectedRoute>} />
            <Route path="/video-creation/submit" element={<ProtectedRoute><Submit /></ProtectedRoute>} />
            <Route path="/video-creation/confirmation" element={<ProtectedRoute><Confirmation /></ProtectedRoute>} />
            
            {/* Greeting Card Flow */}
            <Route path="/greeting/template-selection" element={<GreetingTemplateSelection />} />
            <Route path="/greeting/create" element={<GreetingRecording />} />
            <Route path="/greeting/preview" element={<GreetingPreview />} />
            
            {/* About Us */}
            <Route path="/about" element={<AboutUs />} />

            {/* Features */}
            <Route path="/features" element={<Features />} />

            {/* Contact & Privacy */}
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy-policy" element={<Privacy />} />
            <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
            <Route path="/community-guidelines" element={<CommunityGuidelines />} />

            {/* 404 Page */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </Router>
    </AppProvider>
  );
}

export default App;

